import React from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { AppText } from '../AppText';
import { bookingModeLabel, formatEventWindow } from './siteFormatting';
import Colors from '../../constants/Colors';
import { Site } from '../../types/Site';

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    padding: 16,
  },
  name: { fontWeight: 'bold', fontSize: 16 },
  distance: { color: Colors.grayInfoText, marginTop: 2 },
  summary: { color: Colors.grayInfoText, marginTop: 4 },
  link: {
    color: Colors.primaryColor,
    textDecorationLine: 'underline',
    marginTop: 8,
  },
});

export interface SitePreviewCardProps {
  site: Site;
  onViewDetails: () => void;
  // Miles from the neighbor's own resolved location to this site, computed client-side by the
  // caller -- null when that location is unresolved (e.g. a logged-out, GPS-denied neighbor), in
  // which case no distance is shown at all. No Sites operation vends a neighbor-relative distance:
  // GetSitesWithinRadius measures from the map's search center, and SearchAllSites/DescribeSite
  // carry none, so a server-provided figure would be either wrong or absent here.
  distanceMiles: number | null;
}

function nextEventSummary(site: Site): string | null {
  const [firstEvent] = site.events ?? [];
  if (!firstEvent) return null;
  const [firstWindow] = firstEvent.windows ?? [];
  if (!firstWindow) return null;
  return `${bookingModeLabel(firstEvent.bookingMode)}: ${formatEventWindow(firstWindow)}`;
}

// Mirrors PantryMap.js's pantryDetailsContainer preview card -- shown as a bottom overlay on
// narrow layouts when a site is selected. The whole card is tappable (not just the "View
// details" text), matching that legacy behavior, and navigates to the full details experience
// (the legacy Pantry screen or the new SiteDetails screen) rather than showing everything here.
// Styling is deferred; PantryMap.js's version also shows a photo, which Site has no equivalent
// field for yet.
export function SitePreviewCard({ site, onViewDetails, distanceMiles }: SitePreviewCardProps) {
  const summary = nextEventSummary(site);

  return (
    <TouchableOpacity
      style={styles.container}
      onPress={onViewDetails}
      activeOpacity={0.8}
      testID="site-preview-card"
    >
      <AppText style={styles.name}>{site.name}</AppText>
      {distanceMiles !== null && <AppText style={styles.distance}>{distanceMiles.toFixed(1)} mi</AppText>}
      {summary && <AppText style={styles.summary}>{summary}</AppText>}
      <AppText style={styles.link}>View details</AppText>
    </TouchableOpacity>
  );
}
